import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { InspectionRow } from "@autorepair/contracts";
import { inspectionsApi } from "../lib/api";
import { formatDate, formatInvoiceNumber, formatMoney, formatWorkOrderNumber } from "../lib/money";
import { describeRange } from "../lib/dateRange";
import { useDateRangeParam } from "../lib/useDateRangeParam";
import { DateRangeFilter } from "../components/DateRangeFilter";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernEmptyState,
  ModernErrorBanner,
  ModernPagination,
  ModernTableCard,
  ModernTd,
  ModernTh,
} from "../components/ui-modern";

function csvCell(value: string | number | null): string {
  const text = value === null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The audit export: one line per inspection, dates as YYYY-MM-DD, fee as a plain number. */
function buildCsv(rows: InspectionRow[]): string {
  const header = ["Inspection date", "Result", "VIR / test record #", "Inspector", "Customer", "Vehicle", "VIN", "Plate", "Description", "Fee", "Work order", "Invoice"];
  const lines = rows.map((r) => [
    r.inspectedOn,
    r.result ?? "",
    r.virNumber,
    r.inspectorName,
    r.customerName,
    r.vehicleDescription,
    r.vin,
    r.plate,
    r.description,
    r.fee,
    formatWorkOrderNumber(r.workOrderNumber),
    r.invoiceNumber != null ? formatInvoiceNumber(r.invoiceNumber) : "",
  ]);
  return [header, ...lines].map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const RESULT_STYLE = { pass: "text-[#1e7d32]", fail: "text-[#b3261e]" } as const;

/**
 * State inspection log: every state-inspection line on a work order (see
 * work_order_inspections), by inspection date, with a CSV export so a shop can
 * answer an audit — which vehicle, when, result, VIR #, who did it. Owner and staff.
 */
export function InspectionsPageModern() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [range, setRange] = useDateRangeParam("thisMonth", () => setPage(1));

  const { data, isLoading, error } = useQuery({
    queryKey: ["inspections", range.from, range.to],
    queryFn: () => inspectionsApi.list(range),
  });
  const pagedRows = useMemo(() => (data?.rows ?? []).slice((page - 1) * pageSize, page * pageSize), [data, page, pageSize]);

  function exportCsv(): void {
    if (!data) return;
    const label = range.from || range.to ? `${range.from || "start"}_to_${range.to || "today"}` : "all";
    downloadCsv(`inspections-${label}.csv`, buildCsv(data.rows));
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">State inspections</Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Inspections</h1>
        </div>
        <ModernButton type="button" variant="secondary" onClick={exportCsv} disabled={!data || data.rows.length === 0}>
          Export Inspection Data as CSV
        </ModernButton>
      </div>

      <DateRangeFilter value={range} onChange={setRange} />

      {isLoading && <Spinner label="Loading inspections…" />}
      {error && <ModernErrorBanner message="Could not load inspections." />}

      {data && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Inspections</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#071a33]">{data.total}</div>
              <div className="mt-2 text-[12.5px] text-[#6b7a8f]">{describeRange(range)}</div>
            </ModernCard>
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Passed</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#1e7d32]">{data.passed}</div>
            </ModernCard>
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Failed</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#b3261e]">{data.failed}</div>
              {data.pending > 0 && <div className="mt-2 text-[12.5px] text-[#6b7a8f]">{data.pending} not entered yet</div>}
            </ModernCard>
            <ModernCard>
              <Eyebrow className="text-[11px] text-[#6b7a8f]">Fees</Eyebrow>
              <div className="mt-2.5 text-[30px] font-bold leading-none tabular-nums text-[#071a33]">{formatMoney(data.totalFees)}</div>
            </ModernCard>
          </div>

          {data.rows.length === 0 ? (
            <ModernEmptyState>No state inspections in this date range. Try a wider range.</ModernEmptyState>
          ) : (
            <ModernTableCard>
              <table className="w-full min-w-[980px] border-collapse text-[13.5px]">
                <thead>
                  <tr className="bg-[#f8fafc]">
                    {["Date", "Result", "VIR #", "Inspector", "Customer", "Vehicle", "VIN", "Plate", "Fee", "Work Order", "Invoice"].map((h) => (
                      <ModernTh key={h} align={h === "Fee" ? "right" : undefined} className="px-3.5 py-[11px]">
                        {h}
                      </ModernTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r) => (
                    <tr key={r.inspectionId}>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 tabular-nums">
                        {formatDate(r.inspectedOn)}
                      </ModernTd>
                      <ModernTd valign="middle" className={`whitespace-nowrap px-3.5 py-3 font-bold ${r.result ? RESULT_STYLE[r.result] : "!text-[#8b97a8]"}`}>
                        {r.result ? r.result.toUpperCase() : "—"}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 font-mono text-[12.5px]">
                        {r.virNumber ?? "—"}
                      </ModernTd>
                      <ModernTd valign="middle" className="px-3.5 py-3 text-[#4a5361]">
                        {r.inspectorName ?? "—"}
                      </ModernTd>
                      <ModernTd valign="middle" className="px-3.5 py-3">
                        {r.customerNumber != null ? (
                          <Link to={`/customers/${r.customerNumber}`} className="font-semibold text-[#0f6d86] no-underline hover:underline">
                            {r.customerName}
                          </Link>
                        ) : (
                          r.customerName
                        )}
                      </ModernTd>
                      <ModernTd valign="middle" className="px-3.5 py-3 text-[#4a5361]">
                        {r.vehicleDescription}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 font-mono text-[12.5px] text-[#4a5361]">
                        {r.vin ?? "—"}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3 text-[#4a5361]">
                        {r.plate ?? "—"}
                      </ModernTd>
                      <ModernTd valign="middle" align="right" className="whitespace-nowrap px-3.5 py-3 font-bold tabular-nums">
                        {formatMoney(r.fee)}
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3">
                        <Link
                          to={`/work-orders/${r.workOrderNumber}`}
                          className="font-mono text-[12.5px] font-medium text-[#0f6d86] no-underline hover:underline"
                        >
                          {formatWorkOrderNumber(r.workOrderNumber)}
                        </Link>
                      </ModernTd>
                      <ModernTd valign="middle" className="whitespace-nowrap px-3.5 py-3">
                        {r.invoiceNumber != null ? (
                          <Link
                            to={`/invoices/${r.invoiceNumber}`}
                            className="font-mono text-[12.5px] font-medium text-[#0f6d86] no-underline hover:underline"
                          >
                            {formatInvoiceNumber(r.invoiceNumber)}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </ModernTd>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ModernPagination
                page={page}
                pageSize={pageSize}
                total={data.rows.length}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
              />
            </ModernTableCard>
          )}
        </>
      )}
    </div>
  );
}
